/**
 * TodoFilter.ts
 *
 * Todo 필터링을 담당
 * 각 필터 타입에 따른 todo 항목 필터링 로직 구현
 */

import moment from 'moment'
import { PRIORITY, TODO_STATUS, TodoItem } from './TodoItem'
import * as todoService from './TodoService'
import { TodoDBFilterService } from './todoDB/TodoDBService'

export enum FilterType {
  All = 'all',
  Today = 'today', // Due date 가 오늘로 지정된 할일만 필터링
  Important = 'important', //  Priority 가 High 로 지정된 할일만 필터링
  Planned = 'planned', // Due date 가 지정된 할일만 필터링
  Overdue = 'overdue', //  Due date 가 지난 할일만 필터링. Section 없음
  Completed = 'completed', // 완료된 할일만 필터링. Section 없음.

  // Calendar Day View 에서 사용 (사용자가 지정한 date 에만 표시 - Sort option 은 Today 와 동일)
  FocusDate = 'focusDate', // Due date 가 지정된 날짜와 같은 할일만 필터링.
}

// filter 에 따른 sort type.
export enum SortType {
  DueDate = 'dueDate',
  Reminder = 'reminder',
  Title = 'title', // A - Z - Alphabet ASC
  Created = 'created',

  /**
   * - FilterType.Planned -  Due date 가 지정된 할일만 필터링
   *  Section 내의 할일은 Due date 최신 순으로 정렬.
   * Due date 가 같은 경우 Reminder 최신순으로 정렬,
   *  Reminder 가 같을 경우 Alphabet ASC 로 정렬
   */
  Planned = 'planned',

  /**
   * - FilterType.Overdue -  Due date 가 지난 할일만 필터링. Section 없음
   *  Due date 최신 순으로 정렬. Due date 가 같은 경우 Reminder 최신순으로 정렬,
   * Reminder 가 같을 경우 Alphabet ASC 로 정렬렬
   */
  Overdue = 'overdue',

  /**
   * - FilterType.Completed - 완료된 할일만 필터링. Section 없음.
   */
  Completed = 'completed',
}

export interface FilterCounts {
  [FilterType.All]: number
  [FilterType.Today]: number
  [FilterType.Important]: number
  [FilterType.Planned]: number
  [FilterType.Overdue]: number
  [FilterType.Completed]: number
  [FilterType.FocusDate]: number
}

/**
 * 오늘 할 일 필터 - dueDate 또는 startDate가 오늘이면서 완료되지 않은 todo
 */
const filterTodayTodos = (todos: TodoItem[]): TodoItem[] => {
  const today = moment().startOf('day')

  return todos.filter((todo) => {
    //if (todo.status === TODO_STATUS.COMPLETED) return false

    // Check if dueDate is today
    if (todo.dueDate) {
      const dueDate = moment(todo.dueDate).startOf('day')
      if (dueDate.isSame(today, 'day')) return true
    }

    // Check if startDate is today
    if (todo.startDate) {
      const startDate = moment(todo.startDate).startOf('day')
      if (startDate.isSame(today, 'day')) return true
    }

    return false
  })
}

const filterFocusDateTodos = (todos: TodoItem[], focusDate: Date | null): TodoItem[] => {
  // focusDate가 null이면 오늘 날짜 사용, 있으면 해당 날짜의 시작 시간으로 설정
  const fDate = focusDate ? moment(focusDate).startOf('day') : moment().startOf('day')

  return todos.filter((todo) => {
    return todo.dueDate && moment(todo.dueDate).startOf('day').isSame(fDate, 'day')
  })
}

/**
 * 오늘 완료된 할 일 필터 - dueDate가 오늘이고 완료된 todo
 */
const filterTodayCompletedTodos = (todos: TodoItem[]): TodoItem[] => {
  const today = moment().startOf('day')

  return todos.filter((todo) => {
    if (todo.status !== TODO_STATUS.COMPLETED) return false

    // Check if dueDate is today
    if (todo.dueDate) {
      const dueDate = moment(todo.dueDate).startOf('day')
      if (dueDate.isSame(today, 'day')) return true
    }

    return false
  })
}

/**
 * 중요 할 일 필터 - high priority 설정된 todo
 */
const filterImportantTodos = (todos: TodoItem[]): TodoItem[] => {
  return todos.filter((todo) => {
    return todo.status !== TODO_STATUS.COMPLETED && todo.priority === PRIORITY.HIGH
  })
}

/**
 * 예정된 할 일 필터 - start date 또는 due date가 설정된 todo
 */
const filterPlannedTodos = (todos: TodoItem[]): TodoItem[] => {
  return todos.filter((todo) => {
    return todo.status !== TODO_STATUS.COMPLETED && todo.dueDate
  })
}

/**
 * 연체된 할 일 필터 - due date가 과거인 todo
 */
const filterOverdueTodos = (todos: TodoItem[]): TodoItem[] => {
  const now = moment()

  return todos.filter((todo) => {
    if (todo.status === TODO_STATUS.COMPLETED || !todo.dueDate) return false

    const dueDate = moment(todo.dueDate)
    return dueDate.isBefore(now, 'day')
  })
}

/**
 * 완료된 할 일 필터 - completed 상태인 todo
 */
const filterCompletedTodos = (todos: TodoItem[]): TodoItem[] => {
  return todos.filter((todo) => {
    return todo.status === TODO_STATUS.COMPLETED
  })
}

/**
 * Today 필터에 대한 활성 할일과 완료된 할일을 모두 반환
 */
export const getTodayTodosWithCompleted = async (): Promise<{
  active: TodoItem[]
  completed: TodoItem[]
}> => {
  const allTodos = await todoService.getSyncTodoItems()

  return {
    active: filterTodayTodos(allTodos),
    completed: filterTodayCompletedTodos(allTodos),
  }
}

/**
 * 필터 타입에 따른 todo 목록 반환
 */
export const getFilteredTodoItems = async (
  filterType: FilterType,
  focusDate: Date | null,
): Promise<TodoItem[]> => {
  const allTodos = await todoService.getSyncTodoItems()

  switch (filterType) {
    case FilterType.All:
      return allTodos
    case FilterType.Today:
      // Today filter는 active와 completed 섹션을 모두 포함
      return filterTodayTodos(allTodos)
    case FilterType.FocusDate:
      return filterFocusDateTodos(allTodos, focusDate)
    case FilterType.Important:
      return filterImportantTodos(allTodos)
    case FilterType.Planned:
      return filterPlannedTodos(allTodos)
    case FilterType.Overdue:
      return filterOverdueTodos(allTodos)
    case FilterType.Completed:
      return filterCompletedTodos(allTodos)
    default:
      return []
  }
}

/**
 * 각 filter 의 todo item count 를 계산하여 반환 (캐시 최적화)
 *
 * All, Today, Important, Planned, Overdue 필터에서는 completed 되지 않은 todo 개수
 * Completed 필터는 완료된 todo 개수 표시
 */
export const getFilterCounts = async (): Promise<FilterCounts> => {
  const allTodos = await todoService.getSyncTodoItems()

  // 한 번의 순회로 모든 카운트 계산 (성능 최적화)
  const counts = calculateAllFilterCounts(allTodos)

  return counts
}

/**
 * 한 번의 순회로 모든 필터 카운트 계산
 */
const calculateAllFilterCounts = (allTodos: TodoItem[]): FilterCounts => {
  const today = moment().startOf('day')
  const now = moment()

  let allCount = 0
  let todayCount = 0
  let importantCount = 0
  let plannedCount = 0
  let overdueCount = 0
  let completedCount = 0

  // 단일 순회로 모든 카운트 계산
  allTodos.forEach((todo) => {
    const isCompleted = todo.status === TODO_STATUS.COMPLETED
    const isImportant = todo.priority === PRIORITY.HIGH
    const hasPlannedDate = !!(todo.startDate || todo.dueDate)

    // Today 체크
    let isToday = false
    if (todo.dueDate) {
      const dueDate = moment(todo.dueDate).startOf('day')
      isToday = dueDate.isSame(today, 'day')
    }

    // Overdue 체크
    let isOverdue = false
    if (!isCompleted && todo.dueDate) {
      const dueDate = moment(todo.dueDate)
      isOverdue = dueDate.isBefore(now, 'day')
    }

    // 카운트 증가
    if (isCompleted) {
      completedCount++
    } else {
      allCount++
      if (isToday) todayCount++
      if (isImportant) importantCount++
      if (hasPlannedDate) plannedCount++
      if (isOverdue) overdueCount++
    }
  })

  return {
    [FilterType.All]: allCount,
    [FilterType.Today]: todayCount,
    [FilterType.Important]: importantCount,
    [FilterType.Planned]: plannedCount,
    [FilterType.Overdue]: overdueCount,
    [FilterType.Completed]: completedCount,
    [FilterType.FocusDate]: 0,
  }
}

/**
 * 특정 필터의 개수만 계산
 */
export const getFilterCount = async (filterType: FilterType): Promise<number> => {
  const filteredTodos = await getFilteredTodoItems(filterType, null)
  return filteredTodos.length
}

/**
 * ================================
 * DB 우선 사용 함수들
 * ================================
 */

/**
 * DB 우선 필터링된 Todo 조회
 */
export const getFilteredTodoItemsDB = async (
  filterType: FilterType,
  focusDate: Date | null,
): Promise<TodoItem[]> => {
  try {
    const result = await TodoDBFilterService.getFilteredTodos(filterType, focusDate)
    if (!result) throw new Error('DB returned null')
    return result
  } catch (error) {
    console.error('Failed to get filtered todos from DB, falling back to memory:', error)
    return getFilteredTodoItems(filterType, focusDate)
  }
}

/**
 * DB 우선 필터 카운트 조회
 */
export const getFilterCountsDB = async (): Promise<FilterCounts> => {
  try {
    const counts = await TodoDBFilterService.getFilterCounts()
    if (!counts) throw new Error('DB returned null')
    return {
      [FilterType.All]: counts.all,
      [FilterType.Today]: counts.today,
      [FilterType.Important]: counts.important,
      [FilterType.Planned]: counts.planned,
      [FilterType.Overdue]: counts.overdue,
      [FilterType.Completed]: counts.completed,
      [FilterType.FocusDate]: 0,
    }
  } catch (error) {
    console.error('Failed to get filter counts from DB, falling back to memory:', error)
    return getFilterCounts()
  }
}

/**
 * DB 우선 오늘의 Todo 조회 - debug 용
 */
export const getTodayTodosWithCompletedDB = async (): Promise<{
  active: TodoItem[]
  completed: TodoItem[]
}> => {
  try {
    const result = await TodoDBFilterService.getTodayTodos()
    if (!result) throw new Error('DB returned null')
    return result
  } catch (error) {
    console.error('Failed to get today todos from DB, falling back to memory:', error)
    return getTodayTodosWithCompleted()
  }
}
